/**
 * Connector Console — Connector list with stats, filters, create modal, lifecycle actions.
 * Phase 4.7 — WQ-165
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon, SearchIcon } from '../common/Icons';
import {
  listConnectors, getStats, registerConnector, transitionLifecycle, triggerHealthCheck,
  type Connector, type ConnectorStats,
} from '../../services/connectorService';
import ConnectorDetailDrawer from './ConnectorDetailDrawer';

const TYPE_COLORS: Record<string, string> = {
  mcp_server: '#a855f7',
  sync_adapter: '#3b82f6',
  hybrid: '#14b8a6',
  internal_service: '#6b7280',
};

const STATE_COLORS: Record<string, string> = {
  candidate: '#6b7280',
  testing: '#3b82f6',
  approved: '#06b6d4',
  active: '#22c55e',
  suspended: '#eab308',
  retired: '#dc2626',
};

const TRANSITIONS: Record<string, string[]> = {
  candidate: ['testing', 'retired'],
  testing: ['approved', 'retired'],
  approved: ['active', 'retired'],
  active: ['suspended', 'retired'],
  suspended: ['active', 'retired'],
  retired: ['candidate'],
};

const CONNECTOR_TYPES = ['mcp_server', 'sync_adapter', 'hybrid', 'internal_service'];
const LIFECYCLE_STATES = ['candidate', 'testing', 'approved', 'active', 'suspended', 'retired'];

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function healthColor(c: Connector): string {
  if (!c.health_url) return '#6b7280';
  if (c.last_health_status === 'healthy') return c.consecutive_failures > 0 ? '#eab308' : '#22c55e';
  if (c.last_health_status) return '#dc2626';
  return '#6b7280';
}

function healthLabel(c: Connector): string {
  if (!c.health_url) return 'N/A';
  if (!c.last_health_status) return 'Unchecked';
  if (c.last_health_status === 'healthy') return c.consecutive_failures > 0 ? 'Degraded' : 'Healthy';
  return 'Unhealthy';
}

const ConnectorConsole: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [stats, setStats] = useState<ConnectorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [drawerConnectorId, setDrawerConnectorId] = useState<string | null>(null);
  const [transitionFeedback, setTransitionFeedback] = useState<{
    connectorId: string; message: string; type: 'success' | 'error';
  } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: { connector_type?: string; lifecycle_state?: string } = {};
      if (typeFilter) filters.connector_type = typeFilter;
      if (stateFilter) filters.lifecycle_state = stateFilter;
      const [cList, cStats] = await Promise.all([
        listConnectors(authFetch, filters),
        getStats(authFetch),
      ]);
      setConnectors(cList);
      setStats(cStats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connectors');
    }
    setLoading(false);
  }, [authFetch, typeFilter, stateFilter]);

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading, fetchData]);

  const handleTransition = async (id: string, newState: string) => {
    try {
      const result = await transitionLifecycle(authFetch, id, newState);
      // Show health check result if auto-triggered (→ testing)
      if (result.health_check_result) {
        const hc = result.health_check_result;
        const isHealthy = hc.status === 'healthy';
        setTransitionFeedback({
          connectorId: id,
          message: isHealthy
            ? 'Health check passed — connector is reachable. Ready for approval.'
            : `Health check ${hc.status === 'error' ? 'error' : 'failed'}: ${hc.error || hc.status || 'unknown'}. Review connector configuration.`,
          type: isHealthy ? 'success' : 'error',
        });
        setTimeout(() => setTransitionFeedback(null), 8000);
      }
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transition failed');
    }
  };

  const handleHealthCheck = async (id: string) => {
    try {
      await triggerHealthCheck(authFetch, id);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Health check failed');
    }
  };

  const filtered = connectors.filter(c =>
    !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading && connectors.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading connectors...</div>;
  }

  return (
    <div>
      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div className="glass-card" style={{ padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats.total}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total</div>
          </div>
          <div className="glass-card" style={{ padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#22c55e' }}>{stats.enabled}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Enabled</div>
          </div>
          <div className="glass-card" style={{ padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#6b7280' }}>{stats.disabled}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Disabled</div>
          </div>
          {Object.entries(stats.health_summary).map(([k, v]) => (
            <div key={k} className="glass-card" style={{ padding: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{v}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{k}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
          <SearchIcon size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text" placeholder="Search connectors..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '8px 8px 8px 32px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}>
          <option value="">All Types</option>
          {CONNECTOR_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
        </select>
        <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}>
          <option value="">All States</option>
          {LIFECYCLE_STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setShowCreate(true)} style={{
          fontSize: 13, padding: '6px 16px', borderRadius: 6,
          border: 'none', fontWeight: 500, cursor: 'pointer',
          background: 'var(--accent-primary, #3b82f6)', color: '#fff',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>+ Register</button>
        <button onClick={fetchData} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6,
          border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Connector Table */}
      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {searchQuery || typeFilter || stateFilter
            ? 'No connectors match your filters.'
            : 'No connectors registered. Register your first connector to enable AI tool integrations.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Name</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Type</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>State</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Health</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Enabled</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Invocations</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Last Invoked</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <React.Fragment key={c.id}>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600, cursor: 'pointer', color: 'var(--text-primary)' }}
                    onClick={() => setDrawerConnectorId(c.id)}>{c.name}</td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      background: `${TYPE_COLORS[c.connector_type] || '#888'}20`,
                      color: TYPE_COLORS[c.connector_type] || '#888',
                    }}>{c.connector_type.replace('_', ' ')}</span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 10,
                      background: `${STATE_COLORS[c.lifecycle_state] || '#888'}20`,
                      color: STATE_COLORS[c.lifecycle_state] || '#888',
                    }}>{c.lifecycle_state}</span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: healthColor(c), display: 'inline-block' }} />
                      {healthLabel(c)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    <span style={{ color: c.enabled ? '#22c55e' : '#dc2626', fontWeight: 600, fontSize: 12 }}>
                      {c.enabled ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                    {c.total_invocations.toLocaleString()}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {relativeTime(c.last_invoked_at)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {(TRANSITIONS[c.lifecycle_state] || []).map(ns => (
                        <button key={ns} onClick={() => handleTransition(c.id, ns)}
                          style={{
                            fontSize: 10, padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
                            border: `1px solid ${STATE_COLORS[ns] || '#888'}40`,
                            background: `${STATE_COLORS[ns] || '#888'}10`,
                            color: STATE_COLORS[ns] || '#888',
                          }}>{c.lifecycle_state === 'retired' && ns === 'candidate' ? 'Re-register'
                            : c.lifecycle_state === 'testing' && ns === 'approved'
                              ? (c.last_health_status === 'healthy' ? '✓ Approve' : c.last_health_status ? 'Approve (check failed)' : 'Approve')
                              : ns}</button>
                      ))}
                      {c.health_url && (
                        <button onClick={() => handleHealthCheck(c.id)}
                          style={{
                            fontSize: 10, padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
                            border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                          }}>check</button>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Inline health check feedback after transition */}
                {transitionFeedback?.connectorId === c.id && (
                  <tr>
                    <td colSpan={8} style={{ padding: '0 12px 8px' }}>
                      <div style={{
                        padding: '8px 12px', borderRadius: 6, fontSize: 12,
                        background: transitionFeedback.type === 'success' ? 'rgba(34, 197, 94, 0.08)' : 'rgba(220, 38, 38, 0.08)',
                        color: transitionFeedback.type === 'success' ? '#22c55e' : '#dc2626',
                        border: `1px solid ${transitionFeedback.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(220, 38, 38, 0.2)'}`,
                      }}>
                        {transitionFeedback.message}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && <CreateConnectorModal authFetch={authFetch} onClose={() => setShowCreate(false)} onCreated={fetchData} />}

      {/* Detail Drawer */}
      <ConnectorDetailDrawer
        connectorId={drawerConnectorId}
        onClose={() => setDrawerConnectorId(null)}
        onUpdated={fetchData}
      />
    </div>
  );
};

// --- Create Modal ---

interface CreateModalProps {
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  onClose: () => void;
  onCreated: () => void;
}

const CreateConnectorModal: React.FC<CreateModalProps> = ({ authFetch, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '', connector_type: 'mcp_server', description: '',
    origin: '', origin_country: '', transport: '', endpoint: '', health_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const data: {
        name: string; connector_type: string; description?: string;
        origin?: string; origin_country?: string; transport?: string;
        endpoint?: string; health_url?: string;
      } = { name: form.name.trim(), connector_type: form.connector_type };
      if (form.description.trim()) data.description = form.description.trim();
      if (form.origin.trim()) data.origin = form.origin.trim();
      if (form.origin_country.trim()) data.origin_country = form.origin_country.trim();
      if (form.transport.trim()) data.transport = form.transport.trim();
      if (form.endpoint.trim()) data.endpoint = form.endpoint.trim();
      if (form.health_url.trim()) data.health_url = form.health_url.trim();
      await registerConnector(authFetch, data);
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register connector');
    }
    setSaving(false);
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: '1px solid var(--border)', background: 'var(--bg-secondary)', fontSize: 13,
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div className="glass-card" style={{ position: 'relative', width: 480, maxHeight: '80vh', overflow: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Register Connector</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>x</button>
        </div>
        {error && <div style={{ padding: 8, background: '#fef2f2', color: '#dc2626', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Name *</label>
              <input style={fieldStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Type *</label>
              <select style={fieldStyle} value={form.connector_type} onChange={e => setForm({ ...form, connector_type: e.target.value })}>
                {CONNECTOR_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Description</label>
              <textarea style={{ ...fieldStyle, minHeight: 60 }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Origin</label>
                <input style={fieldStyle} value={form.origin} onChange={e => setForm({ ...form, origin: e.target.value })} placeholder="e.g. Anthropic" />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Country</label>
                <input style={fieldStyle} value={form.origin_country} onChange={e => setForm({ ...form, origin_country: e.target.value })} placeholder="e.g. US" />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Transport</label>
              <input style={fieldStyle} value={form.transport} onChange={e => setForm({ ...form, transport: e.target.value })} placeholder="e.g. stdio, http, sse" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Endpoint</label>
              <input style={fieldStyle} value={form.endpoint} onChange={e => setForm({ ...form, endpoint: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Health URL</label>
              <input style={fieldStyle} value={form.health_url} onChange={e => setForm({ ...form, health_url: e.target.value })} placeholder="https://.../health" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{
              fontSize: 13, padding: '6px 12px', borderRadius: 6,
              border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
              background: 'var(--bg-secondary)', color: 'var(--text-primary)',
            }}>Cancel</button>
            <button type="submit" style={{
              fontSize: 13, padding: '6px 16px', borderRadius: 6,
              border: 'none', fontWeight: 500, cursor: 'pointer',
              background: 'var(--accent-primary, #3b82f6)', color: '#fff',
            }} disabled={saving}>
              {saving ? 'Registering...' : 'Register'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConnectorConsole;
